import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useFullProfile } from '@/hooks/useFullProfile';
import { useI18n } from '@/hooks/useI18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/user-avatar';
import { Badge } from '@/components/ui/badge';
import { Building2, UserPlus, Users, Trash2, CreditCard } from 'lucide-react';
import { toast } from '@/hooks/useToast';
import { AgencyDetailsSkeleton, AdvisorsListSkeleton } from './AgencyPageSkeleton';
import { useStripeAccount } from './hooks/useStripeAccount';

interface AdvisorUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

const AgencyPage = () => {
  const { userData, userRole } = useAuth();
  const { t } = useI18n();
  const { profileLoading } = useFullProfile();

  const [advisorEmail, setAdvisorEmail] = useState('');
  const [advisorFirstName, setAdvisorFirstName] = useState('');
  const [advisorLastName, setAdvisorLastName] = useState('');
  const [linkingAdvisor, setLinkingAdvisor] = useState(false);

  const [advisors, setAdvisors] = useState<AdvisorUser[]>([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(true);

  const stripeAccount = useStripeAccount();

  // For admin: list all agencies
  const isAdmin = userRole === 'admin';
  const canEdit = userRole === 'agency' || userRole === 'admin';

  const agencyId = isAdmin ? null : userData?.id;

  useEffect(() => {
    if (!userData?.id) return;
    fetchAdvisors();
  }, [userData?.id]);

  const fetchAdvisors = async () => {
    if (!userData?.id) return;
    setAdvisorsLoading(true);
    try {
      // Use SECURITY DEFINER RPC to avoid RLS recursion on public.users
      const { data, error } = await supabase.rpc('get_my_advisors' as any);
      if (error) throw error;
      setAdvisors((data as AdvisorUser[]) || []);
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' });
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setAdvisorsLoading(false);
    }
  };

  const handleLinkAdvisor = async () => {
    if (!advisorEmail.trim() || !userData?.id) return;
    setLinkingAdvisor(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-advisor', {
        body: {
          email: advisorEmail.trim().toLowerCase(),
          first_name: advisorFirstName.trim(),
          last_name: advisorLastName.trim(),
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setAdvisorEmail('');
      setAdvisorFirstName('');
      setAdvisorLastName('');
      toast({ variant: 'success', title: t('agency.inviteAdvisor.inviteSent'), description: t('agency.inviteAdvisor.inviteSentDesc', { email: advisorEmail }) });
      // Refresh list to include the newly invited advisor
      await fetchAdvisors();
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' });
    } finally {
      setLinkingAdvisor(false);
    }
  };

  const handleRemoveAdvisor = async (advisorId: string) => {
    try {
      // Use SECURITY DEFINER RPC to avoid RLS recursion on public.users
      const { error } = await supabase.rpc('remove_my_advisor' as any, { p_advisor_id: advisorId });
      if (error) throw new Error(error.message);

      setAdvisors(prev => prev.filter(a => a.id !== advisorId));
      toast({ variant: 'success', title: t('agency.advisors.removed'), description: t('agency.advisors.removedDesc') });
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' });
    }
  };

  const agencyName = userData
    ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email
    : '';

  return (
    <div className="max-w-6xl mx-auto pb-4 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Agency Details */}
        <Card className="md:col-span-5">
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t("agency.details.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profileLoading ? (
              <AgencyDetailsSkeleton />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t("agency.details.name")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {userData?.first_name || "—"}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t("agency.details.lastName")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {userData?.last_name || "—"}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t("agency.details.email")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {userData?.email || "—"}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t("agency.details.role")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {userRole === "agency" && t("agency.roles.agency")}
                    {userRole === "admin" && t("agency.roles.admin")}
                    {userRole === "travel_designer" &&
                      t("agency.roles.travel_designer")}
                    {userRole === "traveler" && t("agency.roles.traveler")}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stripe Connect Dashboard */}
      {(userRole === "agency" || userRole === "admin") && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {t("agency.stripe.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stripeAccount.isActive ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-gray-500">
                  {stripeAccount.isStandard
                    ? t("agency.stripe.dashboardDescriptionStandard")
                    : t("agency.stripe.dashboardDescription")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={stripeAccount.openDashboard}
                  disabled={stripeAccount.openingDashboard}
                  className="flex items-center gap-1.5 flex-shrink-0"
                >
                  {stripeAccount.openingDashboard
                    ? t("agency.stripe.openingDashboard")
                    : stripeAccount.isStandard
                      ? t("agency.stripe.openDashboardStandard")
                      : t("agency.stripe.openDashboard")}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {t("agency.stripe.notConnectedShort")}{" "}
                <Link
                  to="/profile?tab=account-settings"
                  className="text-primary underline underline-offset-2"
                >
                  {t("agency.stripe.goToSettings")}
                </Link>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Advisor Section */}
      {(userRole === "agency" || userRole === "admin") && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                {t("agency.inviteAdvisor.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!canEdit && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    {t("agency.permissions.noEditPermission")}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Input
                  placeholder={t("agency.inviteAdvisor.firstName")}
                  value={advisorFirstName}
                  onChange={(e) => setAdvisorFirstName(e.target.value)}
                  disabled={!canEdit}
                />
                <Input
                  placeholder={t("agency.inviteAdvisor.lastName")}
                  value={advisorLastName}
                  onChange={(e) => setAdvisorLastName(e.target.value)}
                  disabled={!canEdit}
                />
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t("agency.inviteAdvisor.emailPlaceholder")}
                  value={advisorEmail}
                  onChange={(e) => setAdvisorEmail(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && canEdit && handleLinkAdvisor()
                  }
                  className="flex-1"
                  disabled={!canEdit}
                />
                <Button
                  onClick={handleLinkAdvisor}
                  disabled={linkingAdvisor || !advisorEmail.trim() || !canEdit}
                >
                  {linkingAdvisor
                    ? t("agency.inviteAdvisor.sending")
                    : t("agency.inviteAdvisor.sendInvite")}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t("agency.inviteAdvisor.inviteDescription")}
              </p>
            </CardContent>
          </Card>

          {/* Advisors List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Users className="h-4 w-4" />
                {t("agency.advisors.title")}
                {advisors.length > 0 && (
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    ({advisors.length})
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {advisorsLoading ? (
                <AdvisorsListSkeleton />
              ) : advisors.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {t("agency.advisors.noAdvisors")}
                </p>
              ) : (
                <ul className="space-y-3">
                  {advisors.map((advisor) => (
                    <li
                      key={advisor.id}
                      className="flex items-center gap-3 p-2 rounded-lg border border-gray-100 bg-gray-50"
                    >
                      <UserAvatar
                        firstName={advisor.first_name}
                        lastName={advisor.last_name}
                        email={advisor.email}
                        imageUrl={advisor.avatar_url}
                        size="lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {advisor.first_name || ""} {advisor.last_name || ""}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {advisor.email}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-red-500"
                        onClick={() => handleRemoveAdvisor(advisor.id)}
                        title={t("agency.advisors.removeTitle")}
                        disabled={!canEdit}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AgencyPage;
